// Timer checkout-block countdown text utilities, shared by the admin preview
// (web/frontend) and the checkout UI extension. Keep this module free of
// runtime imports (the type-only import below is erased at compile time): the
// extension bundles it into the sandboxed checkout worker, and tree-shaking
// only keeps the worker bundle lean while this file stays self-contained.
import type {
  TimerBlockVariables,
  TimerResetIntervalUnit,
  TimerSeparator,
} from "@intelligems/ig-types";

export const TIME_REMAINING_TOKEN = "{{Time remaining}}";
export const END_DATE_TOKEN = "{{End date}}";

/**
 * Live countdown: `DD:HH:MM:SS` when ≥ 1 day, `H:MM:SS` when ≥ 1h, else
 * `MM:SS`. Clamps to 0.
 */
export function formatTimeRemaining(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (days > 0) {
    return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Absolute end instant as a localized date + time. Formats in `timeZone` when
 * given, otherwise the runtime's own timezone.
 */
export function formatEndDateTime(
  iso: string,
  locale?: string,
  timeZone?: string
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(date);
}

export interface CountdownTextSegment {
  text: string;
  /** True for substituted token values, which render bold. */
  isValue: boolean;
}

/**
 * Substitutes the countdown tokens, splitting the text into segments so
 * renderers can emphasize the dynamic values. An omitted value leaves its
 * token intact as literal text; an empty value drops the token entirely.
 */
export function splitCountdownText(
  text: string,
  values: { timeRemaining?: string; endDate?: string }
): CountdownTextSegment[] {
  const replacements: [token: string, value: string][] = [];
  if (values.timeRemaining !== undefined) {
    replacements.push([TIME_REMAINING_TOKEN, values.timeRemaining]);
  }
  if (values.endDate !== undefined) {
    replacements.push([END_DATE_TOKEN, values.endDate]);
  }

  const segments: CountdownTextSegment[] = [];
  let rest = text;
  while (rest.length > 0) {
    let earliest: { index: number; token: string; value: string } | null = null;
    for (const [token, value] of replacements) {
      const index = rest.indexOf(token);
      if (index !== -1 && (earliest === null || index < earliest.index)) {
        earliest = { index, token, value };
      }
    }
    if (earliest === null) {
      segments.push({ text: rest, isValue: false });
      break;
    }
    if (earliest.index > 0) {
      segments.push({ text: rest.slice(0, earliest.index), isValue: false });
    }
    if (earliest.value !== "") {
      segments.push({ text: earliest.value, isValue: true });
    }
    rest = rest.slice(earliest.index + earliest.token.length);
  }
  return segments;
}

/**
 * A recurring checkout-block timer resets at a wall-clock time ("every day at
 * 5pm"), which is meaningless without a zone — and the checkout extension has
 * no runtime access to the store's Shopify timezone, so it must be resolved
 * and baked into the block at edit time. Every writer of a recurring timer
 * block (the dashboard's settings panel and the MCP tools' server-side write
 * path) calls this so the two can't drift.
 *
 * Returns the variables unchanged unless the block is recurring and its
 * timezone doesn't already match the org's. Returns `null` when the block
 * needs a timezone and the org has none — the caller decides how to surface
 * that, since a timer stamped with a guessed zone fires at the wrong hour.
 */
export function ensureCheckoutBlockRecurringTimezone<
  T extends Pick<TimerBlockVariables, "timerMode" | "resetTimezone">,
>(variables: T, orgTimezone: string | null | undefined): T | null {
  if (variables.timerMode !== "recurring") {
    return variables;
  }
  if (!orgTimezone) {
    return variables.resetTimezone ? variables : null;
  }
  if (variables.resetTimezone === orgTimezone) {
    return variables;
  }
  return { ...variables, resetTimezone: orgTimezone };
}

export const TIMER_UNIT_ORDER = [
  "days",
  "hours",
  "minutes",
  "seconds",
] as const;

export type TimerUnit = (typeof TIMER_UNIT_ORDER)[number];

export interface TimerUnitsShown {
  days: boolean;
  hours: boolean;
  minutes: boolean;
  seconds: boolean;
}

export interface DigitalClockPart {
  unit: TimerUnit;
  value: string;
}

const UNIT_SECONDS: Record<TimerUnit, number> = {
  days: 86_400,
  hours: 3_600,
  minutes: 60,
  seconds: 1,
};

const UNIT_LABELS: Record<TimerUnit, string> = {
  days: "days",
  hours: "hrs",
  minutes: "min",
  seconds: "sec",
};

/**
 * Digital clock digit groups for the shown units, largest first.
 * A hidden unit's time rolls into the next shown smaller unit.
 * Falls back to minutes + seconds if no unit is shown
 */
export function formatDigitalClockParts(
  msRemaining: number,
  shown: TimerUnitsShown
): DigitalClockPart[] {
  const anyShown = TIMER_UNIT_ORDER.some((unit) => shown[unit]);
  const effective: TimerUnitsShown = anyShown
    ? shown
    : { days: false, hours: false, minutes: true, seconds: true };

  let remaining = Math.max(0, Math.floor(msRemaining / 1000));
  const parts: DigitalClockPart[] = [];
  for (const unit of TIMER_UNIT_ORDER) {
    if (!effective[unit]) {
      continue;
    }
    const value = Math.floor(remaining / UNIT_SECONDS[unit]);
    remaining -= value * UNIT_SECONDS[unit];
    parts.push({ unit, value: value.toString().padStart(2, "0") });
  }
  return parts;
}

/** `colon` → "02:14:09", `dot` → "02.14.09", `units` → "02 hrs 14 min 09 sec". */
export function joinDigitalClockParts(
  parts: DigitalClockPart[],
  separator: TimerSeparator
): string {
  if (separator === "units") {
    return parts
      .map((part) => `${part.value} ${UNIT_LABELS[part.unit]}`)
      .join(" ");
  }
  return parts.map((part) => part.value).join(separator === "dot" ? "." : ":");
}

/**
 * What a timer should render right now. Both the admin preview and the live
 * checkout extension drive their output from this single decision so they can't
 * disagree on edge cases (missing/invalid end date, expiry).
 * - `unconfigured`: nothing to count down to — a fixedDate timer with no valid
 *   end date, or a perVisitor timer whose start hasn't resolved yet.
 * - `removed`: expired with `expiredBehavior: "remove"`.
 */
export type TimerRender =
  | { kind: "unconfigured" }
  | { kind: "removed" }
  | { kind: "active"; segments: CountdownTextSegment[] }
  | { kind: "expired"; segments: CountdownTextSegment[] };

interface ZonedDateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * Reads the wall-clock date/time that `ms` represents in `timeZone`, via
 * `Intl.DateTimeFormat` (already relied on by `formatEndDateTime`, so this is
 * a proven-safe API inside the sandboxed checkout extension — no timezone
 * library needed, keeping this module dependency-free).
 */
function getZonedParts(ms: number, timeZone: string): ZonedDateParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(ms));
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/**
 * Converts a wall-clock date/time in `timeZone` to its UTC epoch ms, via the
 * standard "double conversion" trick: treat the wall-clock numbers as if they
 * were UTC to get a naive instant, read what that naive instant's clock face
 * looks like back in `timeZone` to recover the zone's offset, then apply the
 * offset to the naive instant. Accurate for virtually all real-world reset
 * times (the rare exception being a reset scheduled inside a DST-transition
 * gap/overlap itself, which is an acceptable, well-known limitation shared by
 * most timezone libraries).
 */
function zonedTimeToUtcMs(parts: ZonedDateParts, timeZone: string): number {
  const naiveMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const implied = getZonedParts(naiveMs, timeZone);
  const impliedMs = Date.UTC(
    implied.year,
    implied.month - 1,
    implied.day,
    implied.hour,
    implied.minute,
    implied.second
  );
  const offsetMs = impliedMs - naiveMs;
  return naiveMs - offsetMs;
}

const PERIOD_FIELD: Record<TimerResetIntervalUnit, "hour" | "day"> = {
  hour: "hour",
  day: "day",
  week: "day",
};

const PERIOD_STEP: Record<TimerResetIntervalUnit, number> = {
  hour: 1,
  day: 1,
  week: 7,
};

/**
 * Resolves the next recurring reset instant at or after `now`, in epoch ms,
 * or null when the block isn't fully configured yet. The candidate is built
 * from `now`'s wall-clock date in `resetTimezone` (today's date for
 * hour/day, or the next date matching `resetDayOfWeek` for week) plus the
 * mode's time-of-day (the current hour plus `resetMinuteOfHour` for hour,
 * `resetTimeOfDay` otherwise); if that candidate has already passed, it's advanced by one
 * period (a calendar day/week, not a fixed millisecond span, so DST doesn't
 * shift the wall-clock reset time).
 */
function resolveRecurringEndMs(
  variables: TimerBlockVariables,
  now: number
): number | null {
  const {
    resetIntervalUnit,
    resetTimeOfDay,
    resetDayOfWeek,
    resetMinuteOfHour,
    resetTimezone,
  } = variables;
  if (!resetTimezone) {
    return null;
  }
  if (!resetIntervalUnit) {
    return null;
  }
  if (resetIntervalUnit === "hour" && resetMinuteOfHour == null) {
    return null;
  }
  if (resetIntervalUnit !== "hour" && !resetTimeOfDay) {
    return null;
  }
  if (resetIntervalUnit === "week" && !resetDayOfWeek) {
    return null;
  }

  const nowParts = getZonedParts(now, resetTimezone);
  const [hour, minute] =
    resetIntervalUnit === "hour"
      ? [nowParts.hour, resetMinuteOfHour!]
      : resetTimeOfDay!.split(":").map(Number);

  let candidate: ZonedDateParts = {
    year: nowParts.year,
    month: nowParts.month,
    day: nowParts.day,
    hour,
    minute,
    second: 0,
  };

  if (resetIntervalUnit === "week") {
    // ISO weekday (1=Mon..7=Sun) of the candidate's plain calendar date,
    // independent of timezone once we already have the civil Y-M-D.
    const todayIsoWeekday =
      ((new Date(
        Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day)
      ).getUTCDay() +
        6) %
        7) +
      1;
    const daysUntil = (((resetDayOfWeek! - todayIsoWeekday) % 7) + 7) % 7;
    if (daysUntil > 0) {
      candidate = advanceCalendarField(candidate, "day", daysUntil);
    }
  }

  let candidateMs = zonedTimeToUtcMs(candidate, resetTimezone);
  if (candidateMs <= now) {
    candidate = advanceCalendarField(
      candidate,
      PERIOD_FIELD[resetIntervalUnit],
      PERIOD_STEP[resetIntervalUnit]
    );
    candidateMs = zonedTimeToUtcMs(candidate, resetTimezone);
  }
  return candidateMs;
}

/**
 * Adds `amount` of `field` to a civil date, normalizing month/year rollovers
 * via `Date.UTC` (e.g. day 32 of January correctly becomes Feb 1).
 */
function advanceCalendarField(
  parts: ZonedDateParts,
  field: "hour" | "day",
  amount: number
): ZonedDateParts {
  const d = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day + (field === "day" ? amount : 0),
      parts.hour + (field === "hour" ? amount : 0),
      parts.minute,
      parts.second
    )
  );
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
  };
}

/**
 * Resolves the timer's target instant in epoch ms, or null when it can't be
 * shown: a fixedDate timer with a missing/unparseable `endDateTime`, a
 * perVisitor timer whose start hasn't been resolved yet (`perVisitorStartMs`
 * null), or a recurring timer missing its interval/time/timezone config.
 * Callers resolve the perVisitor start themselves (storage in the extension,
 * mount time in the preview) and pass it in. `now` is only used by recurring
 * mode (fixedDate/perVisitor resolve to a single instant regardless of when
 * they're asked), but is required so every caller passes the same clock
 * reading it uses elsewhere for state/render resolution.
 */
export function resolveTimerEndMs(
  variables: TimerBlockVariables,
  perVisitorStartMs: number | null,
  now: number
): number | null {
  if (variables.timerMode === "fixedDate") {
    if (!variables.endDateTime) {
      return null;
    }
    const ms = Date.parse(variables.endDateTime);
    if (Number.isNaN(ms)) {
      return null;
    }
    // The picker offers only minute granularity; floor away the stored seconds
    return Math.floor(ms / 60_000) * 60_000;
  }
  if (variables.timerMode === "recurring") {
    return resolveRecurringEndMs(variables, now);
  }
  if (perVisitorStartMs === null) {
    return null;
  }
  return perVisitorStartMs + variables.durationMinutes * 60_000;
}

export type TimerState = TimerRender["kind"];

/**
 * The state verdict alone, for templates that render their own content (digital
 * clock, inline pill) and only need the shared expiry/configuration decision.
 */
export function resolveTimerState(
  variables: TimerBlockVariables,
  endMs: number | null,
  now: number,
  forceExpired = false
): TimerState {
  if (endMs === null) {
    return "unconfigured";
  }
  if (forceExpired || endMs - now <= 0) {
    return variables.expiredBehavior === "remove" ? "removed" : "expired";
  }
  return "active";
}

/**
 * Maps a resolved end instant to what should render now, with countdown-text
 * segments for the text countdown template. `endDateLabel` is the
 * caller-formatted `{{End date}}` value (each surface formats it in its own
 * locale).
 */
export function resolveTimerRender(
  variables: TimerBlockVariables,
  endMs: number | null,
  now: number,
  endDateLabel: string,
  forceExpired = false
): TimerRender {
  if (endMs === null) {
    return { kind: "unconfigured" };
  }
  const state = resolveTimerState(variables, endMs, now, forceExpired);
  if (state === "removed") {
    return { kind: "removed" };
  }
  if (state === "expired") {
    return {
      kind: "expired",
      segments: [{ text: variables.expiredText, isValue: false }],
    };
  }
  return {
    kind: "active",
    segments: splitCountdownText(variables.countdownText, {
      timeRemaining: formatTimeRemaining(endMs - now),
      endDate: endDateLabel,
    }),
  };
}
